import { useMemo, useEffect } from "react";
import * as THREE from "three";

export interface TerrainData {
  name: string;
  width: number;
  height: number;
  resolution: number;
  extent: {
    minEasting: number;
    maxEasting: number;
    minNorthing: number;
    maxNorthing: number;
  };
  elevations: number[];
  stats: { min: number; max: number; mean: number };
}

interface TerrainMesh3DProps {
  terrain: TerrainData;
  /** Origin offset to align with other 3D content (e.g. tunnel centerline) */
  originOffset?: { x: number; y: number; z: number };
  visible?: boolean;
  opacity?: number;
  wireframe?: boolean;
  colorMode?: "elevation" | "solid";
  color?: string;
  /** Show contour lines */
  contours?: boolean;
  /** Contour interval in meters (default 10) */
  contourInterval?: number;
  /** Enable hillshade lighting */
  hillshade?: boolean;
  /** Optional texture to drape on terrain (e.g. geological map) */
  overlayTexture?: THREE.Texture | null;
  /** Opacity of the overlay texture (0-1) */
  overlayOpacity?: number;
}

export function elevationColor(t: number): [number, number, number] {
  // Green → Yellow → Red gradient
  if (t < 0.5) {
    const s = t / 0.5;
    return [s, 0.55 + s * 0.45, 0.1 * (1 - s)];
  } else {
    const s = (t - 0.5) / 0.5;
    return [1, 1 - s * 0.75, 0];
  }
}

const contourVertexShader = /* glsl */ `
  attribute float elevation;
  attribute vec3 color;
  attribute vec2 terrainUv;
  varying float vElevation;
  varying vec3 vColor;
  varying vec3 vNormal;
  varying vec3 vViewPosition;
  varying vec2 vTerrainUv;

  void main() {
    vElevation = elevation;
    vColor = color;
    vTerrainUv = terrainUv;
    vNormal = normalize(normalMatrix * normal);
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    vViewPosition = -mvPosition.xyz;
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const contourFragmentShader = /* glsl */ `
  uniform float contourInterval;
  uniform float contourWidth;
  uniform vec3 contourColor;
  uniform bool contoursEnabled;
  uniform bool useVertexColors;
  uniform bool hillshadeEnabled;
  uniform vec3 solidColor;
  uniform float opacity;
  uniform bool overlayEnabled;
  uniform sampler2D overlayMap;
  uniform float overlayOpacity;

  varying float vElevation;
  varying vec3 vColor;
  varying vec3 vNormal;
  varying vec3 vViewPosition;
  varying vec2 vTerrainUv;

  void main() {
    vec3 baseColor = useVertexColors ? vColor : solidColor;

    // Apply overlay texture if enabled
    if (overlayEnabled) {
      vec4 texColor = texture2D(overlayMap, vTerrainUv);
      // Blend overlay on top, respecting overlay alpha and opacity
      float a = texColor.a * overlayOpacity;
      baseColor = mix(baseColor, texColor.rgb, a);
    }

    if (hillshadeEnabled) {
      // Two-light hillshade for stronger relief
      vec3 sun = normalize(vec3(0.4, 0.8, 0.3));
      vec3 fill = normalize(vec3(-0.3, 0.5, -0.4));
      float nDotSun = max(dot(vNormal, sun), 0.0);
      float nDotFill = max(dot(vNormal, fill), 0.0);
      // Ambient + sun + fill, with strong contrast
      float shade = 0.2 + 0.6 * nDotSun + 0.2 * nDotFill;
      // Boost contrast
      shade = shade * shade;
      baseColor = baseColor * (0.3 + 1.4 * shade);
    }

    if (contoursEnabled) {
      float e = vElevation / contourInterval;
      float de = fwidth(e);
      float contour = smoothstep(contourWidth * de, 0.0, abs(fract(e - 0.5) - 0.5));

      float eMajor = vElevation / (contourInterval * 5.0);
      float deMajor = fwidth(eMajor);
      float majorContour = smoothstep(contourWidth * 1.5 * deMajor, 0.0, abs(fract(eMajor - 0.5) - 0.5));

      float line = max(contour * 0.6, majorContour);
      baseColor = mix(baseColor, contourColor, line);
    }

    gl_FragColor = vec4(baseColor, opacity);
  }
`;

export function TerrainMesh3D({
  terrain,
  originOffset,
  visible = true,
  opacity = 1,
  wireframe = false,
  colorMode = "elevation",
  color = "#8B7355",
  contours = false,
  contourInterval = 10,
  hillshade = true,
  overlayTexture = null,
  overlayOpacity = 0.85,
}: TerrainMesh3DProps) {
  const geometry = useMemo(() => {
    const { width, height, resolution, extent, elevations, stats } = terrain;
    const ox = originOffset?.x ?? 0;
    const oy = originOffset?.y ?? 0;
    const oz = originOffset?.z ?? 0;

    const positions = new Float32Array(width * height * 3);
    const colors = new Float32Array(width * height * 3);
    const elevAttr = new Float32Array(width * height);
    const uvs = new Float32Array(width * height * 2);

    const elevRange = stats.max - stats.min || 1;

    for (let row = 0; row < height; row++) {
      for (let col = 0; col < width; col++) {
        const idx = row * width + col;
        const elev = elevations[idx];
        const validElev = isNaN(elev) ? stats.mean : elev;

        const worldE = extent.minEasting + col * resolution;
        const worldN = extent.minNorthing + row * resolution;

        const i3 = idx * 3;
        positions[i3] = worldE - ox;       // X = east
        positions[i3 + 1] = worldN - oy;   // Y = north
        positions[i3 + 2] = validElev - oz; // Z = up

        // Store actual elevation for contour shader
        elevAttr[idx] = validElev;

        // UV coordinates for texture overlay (0-1 range across terrain)
        uvs[idx * 2] = col / (width - 1);
        uvs[idx * 2 + 1] = 1.0 - row / (height - 1);

        const t = (validElev - stats.min) / elevRange;
        const [r, g, b] = elevationColor(t);
        colors[i3] = r;
        colors[i3 + 1] = g;
        colors[i3 + 2] = b;
      }
    }

    const indices: number[] = [];
    for (let row = 0; row < height - 1; row++) {
      for (let col = 0; col < width - 1; col++) {
        const tl = row * width + col;
        const tr = tl + 1;
        const bl = (row + 1) * width + col;
        const br = bl + 1;
        indices.push(tl, tr, bl);
        indices.push(tr, br, bl);
      }
    }

    const geom = new THREE.BufferGeometry();
    geom.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geom.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    geom.setAttribute("elevation", new THREE.BufferAttribute(elevAttr, 1));
    geom.setAttribute("terrainUv", new THREE.BufferAttribute(uvs, 2));
    geom.setIndex(indices);
    geom.computeVertexNormals();

    return geom;
  }, [terrain, originOffset]);

  const material = useMemo(() => {
    return new THREE.ShaderMaterial({
      vertexShader: contourVertexShader,
      fragmentShader: contourFragmentShader,
      uniforms: {
        contourInterval: { value: contourInterval },
        contourWidth: { value: 1.0 },
        contourColor: { value: new THREE.Color(0x000000) },
        contoursEnabled: { value: contours },
        useVertexColors: { value: colorMode === "elevation" },
        hillshadeEnabled: { value: hillshade },
        solidColor: { value: new THREE.Color(color) },
        opacity: { value: opacity },
        overlayEnabled: { value: !!overlayTexture },
        overlayMap: { value: overlayTexture || new THREE.Texture() },
        overlayOpacity: { value: overlayOpacity },
      },
      side: THREE.DoubleSide,
      transparent: opacity < 1,
      wireframe,
      extensions: {
        derivatives: true,
      },
    });
  }, [contours, contourInterval, colorMode, color, opacity, wireframe, hillshade, overlayTexture, overlayOpacity]);

  if (!visible) return null;

  return <mesh geometry={geometry} material={material} />;
}
